import { BLOCKS } from '@contentful/rich-text-types';

import { contentfulLcGuideType } from 'types';
import {
  getLearningCenterGuideBySlug,
  getAllLearningCenterGuideSlugs,
} from 'lib/contentful';
import rollbar from 'utils/rollbar';
import Layout from 'layouts';
import { Head } from 'humanity/components/head';
import GuideGatedMasthead from 'legacy/components/masthead/guideGatedMasthead';
import GuideUngatedMasthead from 'legacy/components/masthead/guideUngatedMasthead';
import Text from 'legacy/components/text';
import Section from 'legacy/components/section';
import Breadcrumbs from 'legacy/components/breadcrumbs';
import ParagraphWithImage from 'legacy/components/paragraphWithImage';
import TextBlock from 'legacy/components/textBlock';
import StatisticsSection from 'legacy/components/statisticsSection';
import Anchor from 'legacy/components/anchor';
import LeadCapture from 'legacy/components/leadCapture';
import ValuePropWithImageSection from 'legacy/components/valuePropWithImageSection';
import Heading from 'legacy/components/heading';
import SimpleRichText from 'legacy/components/humanity-v1/primitives/simpleRichText';
import GuideMasthead from 'humanity/sections/guideMasthead';

// TODO: Once all pages using Masthead are migrated to Contentful, we can move this to that component
const mastheadRenderOptions = (page) => ({
  renderNode: {
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Text
        variant="default"
        mt={3}
        color={page.masthead?.backgroundColor === 'blue' ? 'white' : 'blue'}
      >
        {children}
      </Text>
    ),
  },
});

const pwImageRenderOptions = {
  renderNode: {
    [BLOCKS.HEADING_1]: (node, children) => (
      <Heading as="h1" mt={0} fontSize={[5, null, null, 6]}>
        {children}
      </Heading>
    ),
    [BLOCKS.PARAGRAPH]: (node, children) => (
      <Text variant="default" mt={2}>
        {children}
      </Text>
    ),
  },
};

const GuidePage = ({ page }) => {
  const Masthead =
    page.masthead?.type === 'guide gated' ? GuideGatedMasthead : GuideUngatedMasthead;

  const category = page.categoriesCollection?.items?.[0];
  const subcategory = page.subcategoriesCollection?.items?.[0];

  return (
    <Layout
      layout={page.layout}
      type="learningCenter"
      notes={page.notes?.itemsCollection?.items}
      disclosures={page.disclosures?.itemsCollection?.items}
    >
      <Head
        title={page.seoMetadata?.title}
        description={page.seoMetadata?.description}
        url={
          page.seoMetadata?.canonicalUrl?.length
            ? `${process.env.NEXT_PUBLIC_HI_BASE_URL}${page.seoMetadata?.canonicalUrl}`
            : `${process.env.NEXT_PUBLIC_HI_BASE_URL}/learn/guides/${page.slug}/`
        }
        noIndex={page.seoMetadata?.noIndex}
      />
      <Breadcrumbs category={category} subcategory={subcategory} contentType="Guides" />
      {!!page.masthead && (
        <Masthead
          backgroundColor={page.masthead?.backgroundColor}
          eyebrow={page.masthead?.eyebrow}
          heading={page.masthead?.heading}
          image={page.masthead?.desktopImage}
          cta={page.masthead?.cta}
          downloadUrl={page.downloadUrl}
          downloadTitle={page.title}
          form={page.masthead?.form}
          contentPosition={page.masthead?.contentPosition}
        >
          <SimpleRichText
            content={page.masthead?.body?.json}
            renderOptions={mastheadRenderOptions(page)}
          />
        </Masthead>
      )}
      {page.sectionsCollection?.items?.map((section) => {
        switch (section.__typename) {
          case 'GuideMasthead':
            return (
              <GuideMasthead
                key={section?.sys?.id}
                {...section}
                downloadUrl={page.downloadUrl}
                downloadTitle={page.title}
              />
            );
          case 'ParagraphWithImage':
            return (
              <ParagraphWithImage
                key={section.sys?.id}
                section={section}
                renderOptions={pwImageRenderOptions}
                bg="gray100"
              />
            );
          case 'TextBlock':
            return (
              <TextBlock
                key={section.sys?.id}
                heading={section.heading}
                headingPosition={section.headingPosition}
                content={section.content}
                mx="auto"
                maxWidth={720}
              />
            );
          case 'StatisticsSection':
            return (
              <StatisticsSection
                key={section.sys?.id}
                statistics={section.statisticsCollection?.items}
                bg="gray100"
              />
            );
          case 'ValuePropWithImageSection':
            return (
              <ValuePropWithImageSection
                key={section.sys?.id}
                heading={section.heading}
                valueProps={section.valuePropsCollection?.items}
                bg={section?.backgroundColor}
              />
            );
          default:
            return null;
        }
      })}
      {page.leadCapture && (
        <Section>
          <Section.Inner>
            <Anchor id="subscribe" />
            <LeadCapture
              heading={page.leadCapture?.heading}
              paragraph={page.leadCapture?.paragraph}
              form={page.leadCapture?.form}
              disclaimer={page.leadCapture?.disclaimer}
            />
          </Section.Inner>
        </Section>
      )}
    </Layout>
  );
};

GuidePage.propTypes = {
  page: contentfulLcGuideType.isRequired,
};

export const getStaticProps = async ({ params, preview = false }) => {
  try {
    const page = await getLearningCenterGuideBySlug(params.slug, preview);

    if (!page) {
      return { notFound: true };
    }

    return { props: { page }, revalidate: 900 };
  } catch (err) {
    rollbar.error(
      `Error fetching Learning Center guide with slug ${params.slug}`,
      err,
      err.response?.data
    );
    throw new Error(`Error fetching Learning Center guide with slug ${params.slug}`, {
      cause: err,
    });
  }
};

export const getStaticPaths = async () => {
  const pages = await getAllLearningCenterGuideSlugs();

  const paths = pages.map((page) => ({
    params: { slug: page.slug },
  }));

  return { paths, fallback: 'blocking' };
};

export default GuidePage;
